import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, i18n, type MessageLanguages } from '@/i18n'
import { translationService } from '@/services/translation.service'
import { defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'
// Import direct des fichiers de locales pour garantir leur disponibilité
import enLocale from '@/locales/en.json'
import frLocale from '@/locales/fr.json'
import itLocale from '@/locales/it.json'

// Map des locales pour accès rapide
const localeFiles: Record<MessageLanguages, Record<string, string>> = {
  fr: frLocale as Record<string, string>,
  en: enLocale as Record<string, string>,
  it: itLocale as Record<string, string>,
}

// Type pour les langues disponibles
type LanguageInfo = {
  code: MessageLanguages
  name: string
  flag: string
}

export const useI18nStore = defineStore('i18n', () => {
  // État réactif
  const currentLanguage = ref<MessageLanguages>(DEFAULT_LANGUAGE)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const availableLanguages = ref<LanguageInfo[]>([...SUPPORTED_LANGUAGES])

  // Getters computed
  const currentLanguageInfo = computed(() =>
    availableLanguages.value.find(lang => lang.code === currentLanguage.value)
  )

  const isLanguageSupported = computed(
    () =>
      (lang: string): boolean =>
        SUPPORTED_LANGUAGES.some(supportedLang => supportedLang.code === lang)
  )

  /**
   * Initialiser le store avec la langue détectée
   */
  async function initialize(): Promise<void> {
    try {
      const detectedLanguage = (i18n.global as any).locale.value
      currentLanguage.value = detectedLanguage

      // 1. Charger depuis le cache pour un démarrage rapide (vérifie automatiquement le TTL)
      const cachedBackendTranslations = loadTranslationsFromCache(detectedLanguage)
      if (cachedBackendTranslations) {
        // Cache valide, fusionner avec les traductions locales déjà chargées
        // Utiliser directement les imports statiques pour garantir la disponibilité
        const lang = detectedLanguage as MessageLanguages
        let localTranslations: any = (localeFiles[lang] || {}) as Record<string, string>
        try {
          // Vérifier aussi dans i18n.global.messages
          const messages = (i18n.global as any).messages
          if (messages) {
            let i18nLocalMessages: any = {}
            if (messages.value) {
              i18nLocalMessages = messages.value[detectedLanguage] || {}
            } else if (messages[detectedLanguage]) {
              i18nLocalMessages = messages[detectedLanguage]
            }
            if (Object.keys(i18nLocalMessages).length > 0) {
              localTranslations = deepMerge(localTranslations, i18nLocalMessages)
            }
          }
        } catch (_e) {
          console.warn(
            '[i18nStore] Erreur accès messages locaux depuis cache, utilisation imports directs'
          )
        }
        const mergedTranslations = deepMerge(localTranslations, cachedBackendTranslations)
        ;(i18n.global as any).setLocaleMessage(detectedLanguage, mergedTranslations as any)
        // On peut lancer une mise à jour en arrière-plan sans bloquer l'UI
        loadTranslationsFromBackend(detectedLanguage).catch(err => {
          console.warn('[i18nStore] Erreur de mise à jour des traductions en arrière-plan:', err)
        })
      } else {
        // 2. Si le cache est vide (premier chargement), on DOIT attendre le backend pour éviter le FOUC.
        await loadTranslationsFromBackend(detectedLanguage)
      }
      //console.log('[i18n] Initialisé')
    } catch (err: unknown) {
      console.error("[i18nStore] Erreur lors de l'initialisation:", err)
      error.value = "Erreur lors de l'initialisation des traductions"
    }
  }

  /**
   * Changer la langue active
   */
  async function changeLanguage(newLanguage: MessageLanguages): Promise<void> {
    if (newLanguage === currentLanguage.value) {
      return
    }

    if (!isLanguageSupported.value(newLanguage)) {
      throw new Error(`Langue non supportée: ${newLanguage}`)
    }

    try {
      isLoading.value = true
      error.value = null

      // Charger les traductions depuis le backend
      await loadTranslationsFromBackend(newLanguage)

      // Mettre à jour Vue i18n
      ;(i18n.global as any).locale.value = newLanguage
      currentLanguage.value = newLanguage

      // Sauvegarder dans localStorage
      localStorage.setItem('bdc-language', newLanguage)
    } catch (err: unknown) {
      console.error('[i18nStore] Erreur lors du changement de langue:', err)
      error.value = `Erreur lors du changement vers ${newLanguage}`
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Charger les traductions depuis le backend et les fusionner avec Vue i18n
   */
  async function loadTranslationsFromBackend(language: MessageLanguages): Promise<void> {
    try {
      const backendTranslations = await translationService.getTranslationsForLanguage(language)

      // Récupérer les traductions locales de fallback
      // IMPORTANT: Toujours utiliser les fichiers JSON directement pour garantir la disponibilité
      // même si i18n.global.messages n'est pas accessible correctement
      let localTranslations: any = {}
      try {
        // Utiliser directement les imports statiques pour garantir la disponibilité
        const lang = language as MessageLanguages
        localTranslations = (localeFiles[lang] || {}) as Record<string, string>

        // Vérifier aussi dans i18n.global.messages au cas où ils seraient plus à jour
        const i18nMessages = (i18n.global as any).messages
        if (i18nMessages) {
          let i18nLocalMessages: any = {}
          if (typeof i18nMessages === 'object' && 'value' in i18nMessages) {
            i18nLocalMessages = i18nMessages.value?.[language] || {}
          } else if (typeof i18nMessages === 'object' && language in i18nMessages) {
            i18nLocalMessages = i18nMessages[language] || {}
          }

          // Fusionner avec les messages i18n si disponibles (peuvent être plus récents)
          if (Object.keys(i18nLocalMessages).length > 0) {
            localTranslations = deepMerge(localTranslations, i18nLocalMessages)
          }
        }
      } catch (e) {
        console.error('[i18nStore] Erreur accès messages locaux:', e)
        // Fallback: utiliser directement les imports
        localTranslations = localeFiles[language] || {}
      }

      // Fusion profonde pour préserver toutes les clés locales et ajouter/surcharger avec le backend
      // IMPORTANT: target = localTranslations (préserve les locales), source = backendTranslations (surcharge)
      const mergedTranslations = deepMerge(localTranslations, backendTranslations)

      // Mettre à jour Vue i18n
      // IMPORTANT: setLocaleMessage remplace complètement les messages, donc on doit passer les messages fusionnés
      ;(i18n.global as any).setLocaleMessage(language, mergedTranslations as any)

      // Mettre en cache seulement les traductions backend (pas les locales)
      saveTranslationsToCache(language, backendTranslations)
    } catch (err: unknown) {
      console.warn(
        `[i18nStore] Erreur chargement traductions ${language}, utilisation fallback local/cache:`,
        err
      )
    }
  }

  /**
   * Fusion profonde de deux objets de traductions
   */
  function deepMerge(target: any, source: any): any {
    const result = { ...target }

    for (const key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        if (
          typeof source[key] === 'object' &&
          source[key] !== null &&
          typeof target[key] === 'object' &&
          target[key] !== null
        ) {
          result[key] = deepMerge(target[key], source[key])
        } else {
          result[key] = source[key]
        }
      }
    }

    return result
  }

  /**
   * Sauvegarde les traductions dans le localStorage avec TTL de 24h
   */
  function saveTranslationsToCache(language: MessageLanguages, translations: object) {
    try {
      const cacheKey = `bdc-translations-${language}`
      const CACHE_TTL = 24 * 60 * 60 * 1000 // 24 heures
      const cached = {
        data: translations,
        timestamp: Date.now(),
        expiresAt: Date.now() + CACHE_TTL,
        language,
      }
      localStorage.setItem(cacheKey, JSON.stringify(cached))
      //console.log(`[i18nStore] Traductions ${language} sauvegardées dans le cache (TTL 24h)`)
    } catch (e) {
      console.error('[i18nStore] Erreur de sauvegarde des traductions dans le cache:', e)
    }
  }

  /**
   * Charge les traductions depuis le localStorage avec vérification TTL
   */
  function loadTranslationsFromCache(language: MessageLanguages): object | null {
    try {
      const cacheKey = `bdc-translations-${language}`
      const cachedData = localStorage.getItem(cacheKey)
      if (!cachedData) {
        return null
      }

      const cached = JSON.parse(cachedData)
      const now = Date.now()

      // Vérifier si le cache est valide (nouveau format avec TTL)
      if (cached.expiresAt && now < cached.expiresAt && cached.language === language) {
        //console.log(`[i18nStore] Traductions ${language} chargées depuis le cache (valide)`)
        return cached.data
      }

      // Cache expiré ou format ancien, le supprimer
      if (cached.expiresAt && now >= cached.expiresAt) {
        //console.log(`[i18nStore] Cache traductions ${language} expiré, suppression`)
        localStorage.removeItem(cacheKey)
        return null
      }

      // Format ancien (sans TTL), retourner les données mais les considérer comme expirées
      if (!cached.expiresAt && cached.data) {
        // On peut retourner les données en fallback, mais elles seront rechargées
        return cached.data
      }

      return null
    } catch (e) {
      console.error('[i18nStore] Erreur de chargement des traductions depuis le cache:', e)
      return null
    }
  }

  /**
   * Recharger les traductions depuis le backend (vider le cache)
   */
  async function refreshTranslations(): Promise<void> {
    try {
      isLoading.value = true
      error.value = null

      // Vider le cache du service
      translationService.clearCache()

      // Vider le cache local
      localStorage.removeItem(`bdc-translations-${currentLanguage.value}`)

      // Recharger pour la langue actuelle
      await loadTranslationsFromBackend(currentLanguage.value)
    } catch (err: unknown) {
      console.error('[i18nStore] Erreur lors du rechargement:', err)
      error.value = 'Erreur lors du rechargement des traductions'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Récupérer les langues disponibles depuis le backend
   */
  async function loadAvailableLanguages(): Promise<void> {
    try {
      const backendLanguages = await translationService.getLanguages()

      // Filtrer pour ne garder que les langues supportées par le frontend
      const supportedBackendLanguages: LanguageInfo[] = backendLanguages
        .filter(lang => isLanguageSupported.value(lang.code))
        .map(lang => ({
          code: lang.code as MessageLanguages,
          name: lang.name,
          flag: SUPPORTED_LANGUAGES.find(sl => sl.code === lang.code)?.flag || '🌐',
        }))

      if (supportedBackendLanguages.length > 0) {
        availableLanguages.value = supportedBackendLanguages
      }
    } catch (err: unknown) {
      console.warn('[i18nStore] Erreur chargement langues backend, utilisation config locale:', err)
      // En cas d'erreur, on garde la configuration locale
    }
  }

  /**
   * Vérifier si une traduction existe pour une clé donnée
   */
  function hasTranslation(key: string, language?: MessageLanguages): boolean {
    const lang = language || currentLanguage.value
    const messages = (i18n.global as any).messages.value[lang]

    // Naviguer dans l'objet hiérarchique
    const keys = key.split('.')
    let current: any = messages

    for (const k of keys) {
      if (!current || typeof current !== 'object' || !(k in current)) {
        return false
      }
      current = current[k]
    }

    return typeof current === 'string'
  }

  /**
   * Obtenir une traduction avec fallback manuel
   */
  function getTranslation(key: string, fallback?: string): string {
    try {
      const translation = (i18n.global as any).t(key)
      if (translation === key) {
        return fallback || key
      }
      return translation
    } catch {
      return fallback || key
    }
  }

  // Actions exposées
  return {
    // État
    currentLanguage: readonly(currentLanguage),
    isLoading: readonly(isLoading),
    error: readonly(error),
    availableLanguages: readonly(availableLanguages),

    // Getters
    currentLanguageInfo,
    isLanguageSupported,

    // Actions
    initialize,
    changeLanguage,
    refreshTranslations,
    loadAvailableLanguages,
    hasTranslation,
    getTranslation,
    // Exports pour la nouvelle logique de cache
    saveTranslationsToCache,
    loadTranslationsFromCache,
  }
})

// Export des types pour TypeScript
export type I18nStore = ReturnType<typeof useI18nStore>
